/// <reference lib="dom" />
/**
 * webShipVariants.web.ts
 * Five distinct ship hull drawing functions for Canvas2D.
 *
 * Each variant draws a unique silhouette with full-opacity fills and dark
 * outlines for a solid, readable look at small pixel scales.
 *
 * Web-only: `.web.ts` so Metro never bundles it on native.
 *
 * @module webShipVariants
 */

/** Variant draw signature shared by all hull functions. */
export type VariantDrawFn = (
  ctx: CanvasRenderingContext2D,
  sz: number,
  damaged: boolean,
) => void;

/** Fill + stroke a pre-built path for solid shapes. */
function _fillStroke(ctx: CanvasRenderingContext2D): void {
  ctx.fill();
  ctx.stroke();
}

/**
 * Fighter — sleek needle nose with swept wings.
 *
 * @param ctx - Canvas 2D rendering context (stroke already configured).
 * @param sz - Ship size scalar.
 * @param damaged - Whether the ship is on fire.
 */
export function drawFighter(ctx: CanvasRenderingContext2D, sz: number, damaged: boolean): void {
  const hull = damaged ? '#8C8280' : '#A0B8D0';
  const accent = damaged ? '#C84828' : '#4888C8';
  const cockpit = damaged ? '#FF7832' : '#60C8FF';

  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.moveTo(sz * 0.2, 0);
  ctx.lineTo(-sz * 1.6, -sz * 0.25);
  ctx.lineTo(-sz * 1.8, 0);
  ctx.lineTo(-sz * 1.6, sz * 0.25);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.moveTo(-sz * 0.6, -sz * 0.15);
  ctx.lineTo(-sz * 1.4, -sz * 0.6);
  ctx.lineTo(-sz * 1.6, -sz * 0.3);
  ctx.closePath();
  _fillStroke(ctx);
  ctx.beginPath();
  ctx.moveTo(-sz * 0.6, sz * 0.15);
  ctx.lineTo(-sz * 1.4, sz * 0.6);
  ctx.lineTo(-sz * 1.6, sz * 0.3);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = cockpit;
  ctx.beginPath();
  ctx.ellipse(-sz * 0.2, 0, sz * 0.18, sz * 0.1, 0, 0, Math.PI * 2);
  _fillStroke(ctx);

  ctx.fillStyle = damaged ? 'rgba(255,80,20,0.7)' : 'rgba(80,160,255,0.7)';
  ctx.beginPath();
  ctx.ellipse(-sz * 2.0, 0, sz * 0.3, sz * 0.1, 0, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Freighter — boxy cargo hauler with container pods.
 *
 * @param ctx - Canvas 2D rendering context (stroke already configured).
 * @param sz - Ship size scalar.
 * @param damaged - Whether the ship is on fire.
 */
export function drawFreighter(ctx: CanvasRenderingContext2D, sz: number, damaged: boolean): void {
  const hull = damaged ? '#786E64' : '#98A8B4';
  const cargo = damaged ? '#645A50' : '#88A0B0';
  const cockpit = damaged ? '#FF6428' : '#48C8B4';

  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.rect(-sz * 1.8, -sz * 0.3, sz * 2.0, sz * 0.6);
  _fillStroke(ctx);

  ctx.fillStyle = cargo;
  ctx.beginPath();
  ctx.rect(-sz * 1.4, -sz * 0.5, sz * 0.8, sz * 0.2);
  _fillStroke(ctx);
  ctx.beginPath();
  ctx.rect(-sz * 1.4, sz * 0.3, sz * 0.8, sz * 0.2);
  _fillStroke(ctx);
  ctx.beginPath();
  ctx.rect(-sz * 0.4, -sz * 0.45, sz * 0.5, sz * 0.15);
  _fillStroke(ctx);

  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.moveTo(sz * 0.2, 0);
  ctx.lineTo(0, -sz * 0.25);
  ctx.lineTo(0, sz * 0.25);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = cockpit;
  ctx.beginPath();
  ctx.rect(-sz * 0.1, -sz * 0.1, sz * 0.2, sz * 0.2);
  _fillStroke(ctx);

  ctx.fillStyle = damaged ? 'rgba(255,70,20,0.7)' : 'rgba(100,180,255,0.7)';
  ctx.beginPath();
  ctx.arc(-sz * 2.0, -sz * 0.15, sz * 0.12, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-sz * 2.0, sz * 0.15, sz * 0.12, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Interceptor — angular stealth shape with twin tails and canards.
 *
 * @param ctx - Canvas 2D rendering context (stroke already configured).
 * @param sz - Ship size scalar.
 * @param damaged - Whether the ship is on fire.
 */
export function drawInterceptor(ctx: CanvasRenderingContext2D, sz: number, damaged: boolean): void {
  const hull = damaged ? '#645F6E' : '#7888A8';
  const wing = damaged ? '#964632' : '#6078B4';
  const cockpit = damaged ? '#FF823C' : '#78DCFF';

  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.moveTo(sz * 0.3, 0);
  ctx.lineTo(-sz * 0.4, -sz * 0.35);
  ctx.lineTo(-sz * 1.6, -sz * 0.15);
  ctx.lineTo(-sz * 1.8, 0);
  ctx.lineTo(-sz * 1.6, sz * 0.15);
  ctx.lineTo(-sz * 0.4, sz * 0.35);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = wing;
  ctx.beginPath();
  ctx.moveTo(-sz * 1.3, -sz * 0.15);
  ctx.lineTo(-sz * 1.9, -sz * 0.55);
  ctx.lineTo(-sz * 2.0, -sz * 0.2);
  ctx.closePath();
  _fillStroke(ctx);
  ctx.beginPath();
  ctx.moveTo(-sz * 1.3, sz * 0.15);
  ctx.lineTo(-sz * 1.9, sz * 0.55);
  ctx.lineTo(-sz * 2.0, sz * 0.2);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = wing;
  ctx.beginPath();
  ctx.moveTo(-sz * 0.1, -sz * 0.2);
  ctx.lineTo(-sz * 0.5, -sz * 0.5);
  ctx.lineTo(-sz * 0.6, -sz * 0.25);
  ctx.closePath();
  _fillStroke(ctx);
  ctx.beginPath();
  ctx.moveTo(-sz * 0.1, sz * 0.2);
  ctx.lineTo(-sz * 0.5, sz * 0.5);
  ctx.lineTo(-sz * 0.6, sz * 0.25);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = cockpit;
  ctx.beginPath();
  ctx.ellipse(0, 0, sz * 0.15, sz * 0.06, 0, 0, Math.PI * 2);
  _fillStroke(ctx);

  ctx.fillStyle = damaged ? 'rgba(255,90,20,0.7)' : 'rgba(90,170,255,0.7)';
  ctx.beginPath();
  ctx.ellipse(-sz * 2.0, -sz * 0.1, sz * 0.2, sz * 0.07, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(-sz * 2.0, sz * 0.1, sz * 0.2, sz * 0.07, 0, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Bomber — heavy, stubby with big engine and bomb bay.
 *
 * @param ctx - Canvas 2D rendering context (stroke already configured).
 * @param sz - Ship size scalar.
 * @param damaged - Whether the ship is on fire.
 */
export function drawBomber(ctx: CanvasRenderingContext2D, sz: number, damaged: boolean): void {
  const hull = damaged ? '#6E645A' : '#96A0AA';
  const under = damaged ? '#5A5046' : '#788290';
  const cockpit = damaged ? '#FF6E28' : '#58D2C8';

  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.moveTo(sz * 0.1, -sz * 0.15);
  ctx.lineTo(-sz * 1.5, -sz * 0.35);
  ctx.lineTo(-sz * 1.8, -sz * 0.3);
  ctx.lineTo(-sz * 1.8, sz * 0.3);
  ctx.lineTo(-sz * 1.5, sz * 0.35);
  ctx.lineTo(sz * 0.1, sz * 0.15);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = under;
  ctx.beginPath();
  ctx.rect(-sz * 1.2, sz * 0.15, sz * 0.9, sz * 0.25);
  _fillStroke(ctx);

  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.moveTo(-sz * 0.8, -sz * 0.3);
  ctx.lineTo(-sz * 1.2, -sz * 0.7);
  ctx.lineTo(-sz * 1.5, -sz * 0.5);
  ctx.lineTo(-sz * 1.3, -sz * 0.3);
  ctx.closePath();
  _fillStroke(ctx);
  ctx.beginPath();
  ctx.moveTo(-sz * 0.8, sz * 0.3);
  ctx.lineTo(-sz * 1.2, sz * 0.7);
  ctx.lineTo(-sz * 1.5, sz * 0.5);
  ctx.lineTo(-sz * 1.3, sz * 0.3);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = cockpit;
  ctx.beginPath();
  ctx.arc(-sz * 0.1, 0, sz * 0.14, 0, Math.PI * 2);
  _fillStroke(ctx);

  ctx.fillStyle = damaged ? 'rgba(255,70,15,0.7)' : 'rgba(100,190,255,0.7)';
  ctx.beginPath();
  ctx.ellipse(-sz * 2.1, 0, sz * 0.4, sz * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Corvette — elegant curved hull with dorsal fin.
 *
 * @param ctx - Canvas 2D rendering context (stroke already configured).
 * @param sz - Ship size scalar.
 * @param damaged - Whether the ship is on fire.
 */
export function drawCorvette(ctx: CanvasRenderingContext2D, sz: number, damaged: boolean): void {
  const hull = damaged ? '#82787D' : '#B4B9C8';
  const fin = damaged ? '#AA4632' : '#7890BE';
  const cockpit = damaged ? '#FF8C3C' : '#6EE6FF';

  ctx.fillStyle = hull;
  ctx.beginPath();
  ctx.moveTo(sz * 0.3, 0);
  ctx.quadraticCurveTo(0, -sz * 0.3, -sz * 1.0, -sz * 0.25);
  ctx.lineTo(-sz * 1.7, -sz * 0.15);
  ctx.lineTo(-sz * 1.8, 0);
  ctx.lineTo(-sz * 1.7, sz * 0.15);
  ctx.lineTo(-sz * 1.0, sz * 0.2);
  ctx.quadraticCurveTo(0, sz * 0.25, sz * 0.3, 0);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = fin;
  ctx.beginPath();
  ctx.moveTo(-sz * 0.5, -sz * 0.25);
  ctx.lineTo(-sz * 0.9, -sz * 0.65);
  ctx.lineTo(-sz * 1.2, -sz * 0.25);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.beginPath();
  ctx.moveTo(-sz * 0.7, sz * 0.18);
  ctx.lineTo(-sz * 0.9, sz * 0.35);
  ctx.lineTo(-sz * 1.1, sz * 0.18);
  ctx.closePath();
  _fillStroke(ctx);

  ctx.fillStyle = cockpit;
  ctx.beginPath();
  ctx.ellipse(sz * 0.05, -sz * 0.05, sz * 0.16, sz * 0.12, -0.2, 0, Math.PI * 2);
  _fillStroke(ctx);

  ctx.fillStyle = damaged ? 'rgba(255,85,20,0.7)' : 'rgba(90,180,255,0.7)';
  ctx.beginPath();
  ctx.ellipse(-sz * 2.0, 0, sz * 0.25, sz * 0.1, 0, 0, Math.PI * 2);
  ctx.fill();
}
